import {
  ColScaffold,
  RowScaffold,
  ScaffoldDropZone,
  ScaffoldHeaderCellSpans,
} from "../../scaffold";
import { EditorDefns, TlfViewModeConfig } from "../../utility";
import * as ARS from "../ars_ldm";
import { ARSStateUpdate } from "../contexts";

// #region sidebar
/**
 * Discriminated union type for sidebar item data, supporting grouping factors,
 * analysis methods, and operations.
 */
export enum ArsTableSidebarItemKind {
  GroupingFactor = "groupingFactor",
  AnalysisMethod = "analysisMethod",
  Operation = "operation",
}

export interface ArsTableSidebarItemAnalysisMethod {
  kind: ArsTableSidebarItemKind.AnalysisMethod;
  dropItem: ARS.AnalysisMethod;
}
export interface ArsTableSidebarItemOperation {
  kind: ArsTableSidebarItemKind.Operation;
  dropItem: ARS.Operation;
}
export interface ArsTableSidebarItemGroupingFactor {
  kind: ArsTableSidebarItemKind.GroupingFactor;
  dropItem: ARS.GroupingFactor;
}
export type ArsTableSidebarItemData =
  | ArsTableSidebarItemGroupingFactor
  | ArsTableSidebarItemAnalysisMethod
  | ArsTableSidebarItemOperation;
// #endregion

// #region table groups
export interface ArsTableOperation<T = string> {
  id: string;
  decimalAlignPercentage?: number;
  dimension?: number;
  hidden?: boolean;
  label: T;
  operation: ARS.Operation;
}

export interface ArsTableSubGroupAnalysisMethod<T = string> {
  id: string;
  analysisMethod: ARS.AnalysisMethod;
  dimension?: number;
  hidden?: boolean;
  label: T;
  subGroups: ArsTableGroup<T>[];
}

export interface ArsTableSubGroupGroup<T = string> {
  id: string;
  dimension?: number;
  group: ARS.Group;
  hidden?: boolean;
  isDataDriven?: boolean;
  label: T;
  subGroups?: ArsTableGroup<T>[];
}

export interface ArsTableSubGroupGroupingFactor<T = string> {
  id: string;
  dimension?: number;
  groupingFactor: ARS.GroupingFactor;
  hidden?: boolean;
  label: T;
  subGroups?: ArsTableGroup<T>[];
}

export type ArsTableSubGroup<T = string> =
  | ArsTableSubGroupAnalysisMethod<T>
  | ArsTableSubGroupGroup<T>
  | ArsTableSubGroupGroupingFactor<T>;

export type ArsTableGroup<T = string> = ArsTableSubGroup<T> | ArsTableOperation<T>;
// #endregion

// #region Cell types
export const defaultColumnWidth = 120;
export const defaultRowHeight = 40;

export enum ArsTableCellType {
  ColumnGroupCell = "columnGroupCell",
  ColumnGroupLabel = "columnGroupLabel",
  ColumnOperation = "columnOperation",
  DataCell = "dataCell",
  RowGroupCell = "rowGroupCell",
  RowGroupLabel = "rowGroupLabel",
  RowHeader = "rowHeader",
  RowOperation = "rowOperation",
}

export interface ArsTableCellBase<T = string> {
  annotationContent: string;
  cellType: ArsTableCellType;
  columnId: string;
  columnIndex: number;
  dataPath: string;
  disabled?: boolean;
  displayContent: T;
  hidden?: boolean;
  rowId: string;
}

export interface ArsTableColumnGroupCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnGroupCell;
  colSpan?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "groupingFactor" | "analysisMethod";
  width?: number;
}

export interface ArsTableColumnGroupLabel<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnGroupLabel;
  colSpan?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "group" | "dataDriven";
  width?: number;
}

export interface ArsTableColumnOperationCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnOperation;
  decimalAlignPercentage?: number;
  hidden?: boolean;
  targetId: string;
  targetType: "operation";
  width?: number;
}

export interface ArsTableDataCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.DataCell;
}

export interface ArsTableRowGroupCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowGroupCell;
  colSpan?: number;
  height?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "groupingFactor" | "analysisMethod";
}

export interface ArsTableRowGroupLabel<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowGroupLabel;
  colSpan?: number;
  height?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "group" | "dataDriven";
}

export interface ArsTableRowHeaderCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowHeader;
  rowSpan?: number;
  width?: number;
}

export interface ArsTableRowOperationCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowOperation;
  decimalAlignPercentage?: number;
  height?: number;
  hidden?: boolean;
  targetId: string;
  targetType: "operation";
}

/**
 * Cell types that represent row-header hierarchy cells.
 */
export const arsTableRowHeaderCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.RowGroupCell,
  ArsTableCellType.RowGroupLabel,
  ArsTableCellType.RowOperation,
]);

/**
 * Cell types that represent column-header hierarchy cells.
 */
export const arsTableColumnHeaderCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.ColumnGroupCell,
  ArsTableCellType.ColumnGroupLabel,
  ArsTableCellType.ColumnOperation,
]);

/**
 * Cell types that represent the "box" area in the top-left of the table
 * which is outside both row and column hierarchies.
 */
export const arsTableBoxCellTypes = new Set<ArsTableCellType>([ArsTableCellType.RowHeader]);

/**
 * Cell lable types
 */
export const areTableHeaderLabelCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.ColumnGroupLabel,
  ArsTableCellType.RowGroupLabel,
]);

export const arsTableHeaderCellTypes = new Set<ArsTableCellType>([
  ...arsTableColumnHeaderCellTypes,
  ...arsTableRowHeaderCellTypes,
]);

export type ArsTableCellWithTarget<T = string> =
  | ArsTableColumnGroupCell<T>
  | ArsTableColumnGroupLabel<T>
  | ArsTableColumnOperationCell<T>
  | ArsTableRowGroupCell<T>
  | ArsTableRowGroupLabel<T>
  | ArsTableRowOperationCell<T>;

export type ArsTableHeaderCell<T = string> =
  | ArsTableColumnGroupCell<T>
  | ArsTableColumnGroupLabel<T>
  | ArsTableColumnOperationCell<T>
  | ArsTableRowHeaderCell<T>;

export type ArsTableBodyCell<T = string> =
  | ArsTableDataCell<T>
  | ArsTableRowGroupCell<T>
  | ArsTableRowGroupLabel<T>
  | ArsTableRowOperationCell<T>;

export type ArsTableCellData<T = string> = ArsTableBodyCell<T> | ArsTableHeaderCell<T>;
// #endregion

// #region Table Row and Body types
export interface ArsTableHeaderRow<T = string> {
  arsTableCells: ArsTableHeaderCell<T>[];
  height: number;
}

export interface ArsTableBodyRow<T = string> {
  arsTableCells: ArsTableBodyCell<T>[];
  height: number;
  rowGroup: number;
}

export type ArsTableRow<T = string> = ArsTableHeaderRow<T> | ArsTableBodyRow<T>;

/**
 * Represents a single column in the row-header area of an ARS table.
 * Stores the column label, per-body-row heights, and column width.
 */
export interface ArsTableRowHeader<T = string> {
  heights: number[];
  label: T;
  width: number;
}

export interface ArsTableBody<T = string> extends Record<
  string,
  string | undefined | ArsTableGroup<T>[] | ArsTableRowHeader<T>[]
> {
  id: string;
  analysisMethodId: string;
  analysisSetId: string;
  bodyType: "arsTable";
  columns: ArsTableGroup<T>[];
  dataSubsetId?: string;
  rowHeaders: ArsTableRowHeader<T>[];
  rows: ArsTableGroup<T>[];
}
// #endregion

/**
 * Derived scaffold model used by ArsTableContent for rendering.
 */
export interface ArsTableScaffoldModel<T = string> extends Record<
  string,
  | Record<string, Record<string, ArsTableCellData<T>>>
  | ColScaffold<ArsTableCellData<T>>[]
  | RowScaffold<ArsTableCellData<T>>[]
  | ScaffoldHeaderCellSpans
> {
  cellData: Record<string, Record<string, ArsTableCellData<T>>>;
  cols: ColScaffold<ArsTableCellData<T>>[];
  rows: RowScaffold<ArsTableCellData<T>>[];
  headerColSpans: ScaffoldHeaderCellSpans;
}

export enum ArsTableJustification {
  Left = "left",
  Center = "center",
  Right = "right",
}

export interface ArsTableSettings<T = string> {
  id: string;
  currentSidebar: {
    filter: string;
    setId: string;
    view: "groupingFactor" | "operations";
  };
  disabled: boolean;
  editorProps: EditorDefns<T>;
  justification: ArsTableJustification;
  showSidebar: boolean;
  sidebarWidth: string;
  tlfViewMode: TlfViewModeConfig;
}

export interface ArsTableState<T = string> {
  bodyData: ArsTableBody<T>;
  scaffoldModel: ArsTableScaffoldModel<T> | null;
  calculatedTableRows: ArsTableRow<T>[] | null;
  changed: boolean;
  settings: ArsTableSettings<T>;
  usedGroupingFactorIds: Set<string>;
}

/**
 *  Action types for ArsTable reducer
 */
export const ARST_CHANGE_SENT = "ARST_CHANGE_SENT";
export const ARST_LOAD_DATA = "ARST_LOAD_DATA";
export const ARST_PATCH_DATA = "ARST_PATCH_DATA";
export const ARST_SET_SCAFFOLD_COL_WIDTH = "ARST_SET_SCAFFOLD_COL_WIDTH";
export const ARST_SET_SCAFFOLD_ROW_HEIGHT = "ARST_SET_SCAFFOLD_ROW_HEIGHT";
export const ARST_SET_SIDEBAR_FILTER = "ARST_SET_SIDEBAR_FILTER";
export const ARST_SET_SIDEBAR_VIEW = "ARST_SET_SIDEBAR_VIEW";
export const ARST_TOGGLE_SIDEBAR = "ARST_TOGGLE_SIDEBAR";
export const ARST_SET_TLF_VIEW_MODE = "ARST_SET_TLF_VIEW_MODE";
export const ARST_UPDATE_SCAFFOLD = "ARST_UPDATE_SCAFFOLD";

export interface ArsTableCellActionConfig {
  dropActions: ArsTableDropActionDefinition[];
  contextMenuActions: ArsTableContextMenuActionDefinition[];
}

export interface ARSTActionChangeSent {
  operation: typeof ARST_CHANGE_SENT;
}

export interface ARSTActionLoadData<T = string> {
  operation: typeof ARST_LOAD_DATA;
  bodyData: ArsTableBody<T>;
}

export interface ARSTActionPatchData extends ARSStateUpdate {
  operation: typeof ARST_PATCH_DATA;
}
export interface ARSTActionPatchDataMultiple {
  operation: typeof ARST_PATCH_DATA;
  /** Multiple updates for atomic application */
  updates: ARSStateUpdate[];
}

export interface ARSTActionSetScaffoldColWidth {
  operation: typeof ARST_SET_SCAFFOLD_COL_WIDTH;
  colId: string;
  width: number;
}

export interface ARSTActionSetScaffoldRowHeight {
  operation: typeof ARST_SET_SCAFFOLD_ROW_HEIGHT;
  rowId: string;
  height: number;
}

export interface ARSTActionSetSidebarView {
  operation: typeof ARST_SET_SIDEBAR_VIEW;
  view: "groupingFactor" | "operations";
}

export interface ARSTActionSetSidebarFilter {
  operation: typeof ARST_SET_SIDEBAR_FILTER;
  filter: string;
}

export interface ARSTActionToggleSidebar {
  operation: typeof ARST_TOGGLE_SIDEBAR;
}

export interface ARSTActionSetTlfViewMode {
  operation: typeof ARST_SET_TLF_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

export interface ARSTActionUpdateScaffold<T = string> {
  operation: typeof ARST_UPDATE_SCAFFOLD;
  calculatedTableRows: ArsTableRow<T>[];
}

/**
 * Context-menu actions
 */
export const ARST_REMOVE_GROUPINGFACTOR = "ARST_REMOVE_GROUPINGFACTOR";
export const ARST_REMOVE_OPERATION = "ARST_REMOVE_OPERATION";
export const ARST_TOGGLE_GROUP_LABEL_VISIBILITY = "ARST_TOGGLE_GROUP_LABEL_VISIBILITY";
export type ArstContextMenuOperation =
  | typeof ARST_REMOVE_GROUPINGFACTOR
  | typeof ARST_REMOVE_OPERATION
  | typeof ARST_TOGGLE_GROUP_LABEL_VISIBILITY;

export interface ArsTableContextMenuActionDefinition {
  label: string;
  operation: ArstContextMenuOperation;
  newValue?: unknown;
}

export interface ARSTActionContextmenuAction<T = string> {
  operation: ArstContextMenuOperation;
  cellValue: ArsTableCellData<T>;
  newValue?: unknown;
}

/**
 * Drop actions
 */
export const ARST_ADD_OPERATION_TO_GROUP = "ARST_ADD_OPERATION_TO_GROUP";
export const ARST_INSERT_COLUMN = "ARST_INSERT_COLUMN";
export const ARST_INSERT_ROW = "ARST_INSERT_ROW";
export const ARST_INSERT_OPERATION_TO_GROUP = "ARST_INSERT_OPERATION_TO_GROUP";
export const ARST_INSERT_SUBGROUP = "ARST_INSERT_SUBGROUP";
export type ArstDropOperation =
  | typeof ARST_ADD_OPERATION_TO_GROUP
  | typeof ARST_INSERT_COLUMN
  | typeof ARST_INSERT_ROW
  | typeof ARST_INSERT_OPERATION_TO_GROUP
  | typeof ARST_INSERT_SUBGROUP;

/**
 * Drop zones on the left edge of a cell (insert-before).
 */
export const LEFT_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
  ScaffoldDropZone.BottomLeft,
]);

/**
 * Drop zones on the right edge of a cell (insert-after).
 */
export const RIGHT_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Drop zones on the top edge of a cell.
 */
export const TOP_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
]);

/**
 * Drop zones on the bottom edge of a cell.
 */
export const BOTTOM_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
]);

/**
 * Drop zones in the centre quadrants of a cell.
 */
export const CENTRE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.CentreTopLeft,
  ScaffoldDropZone.CentreTopRight,
  ScaffoldDropZone.CentreBottomLeft,
  ScaffoldDropZone.CentreBottomRight,
]);

export interface ArsTableDropActionDefinition {
  operation: ArstDropOperation;
  droppedDataKinds: ArsTableSidebarItemKind[];
  dropZones: ScaffoldDropZone[];
}

export interface ARSTActionDropAction {
  operation: ArstDropOperation;
  rowId: string;
  columnId: string;
  payload: ArsTableSidebarItemData[];
  zone: ScaffoldDropZone;
}

export type ArsTableAction<T = string> =
  | ARSTActionContextmenuAction<T>
  | ARSTActionDropAction
  | ARSTActionChangeSent
  | ARSTActionLoadData<T>
  | ARSTActionPatchData
  | ARSTActionPatchDataMultiple
  | ARSTActionSetSidebarFilter
  | ARSTActionSetSidebarView
  | ARSTActionSetTlfViewMode
  | ARSTActionToggleSidebar
  | ARSTActionSetScaffoldColWidth
  | ARSTActionSetScaffoldRowHeight
  | ARSTActionUpdateScaffold<T>;
